/**
 * Subscription tier / flat-rate pricing for Choose Plan / Change Plan dialogs.
 * USD: {@link ./insightPricingDetailsUsd.json}
 * JPY (Japan): {@link ./insightPricingDetailsJpy.json}
 */
import insightPricingDetailsUsd from './insightPricingDetailsUsd.json';
import insightPricingDetailsJpy from './insightPricingDetailsJpy.json';

/** @typedef {'insight' | 'premium' | 'pro' | 'japan'} PricingCategory */
/** @typedef {'monthly' | 'oneYear' | 'threeYear'} PricingPlanKey */
/** @typedef {'old' | 'tiered'} PremiumPricingMode */

export const INSIGHT_PRICING_CURRENCY = insightPricingDetailsUsd.currency || 'USD';
export const JAPAN_PRICING_CURRENCY = insightPricingDetailsJpy.currency || 'JPY';

const DEFAULT_CATEGORY = 'insight';
const JAPAN_CATEGORY = 'japan';
const DEFAULT_PREMIUM_PRICING_MODE = 'old';

/**
 * Active premium pricing variant. Set `premiumPricingMode` in JSON to switch old flat-rate vs tiered.
 * @returns {PremiumPricingMode}
 */
export function getPremiumPricingMode() {
  const mode = insightPricingDetailsUsd.premiumPricingMode;
  if (mode === 'old' || mode === 'tiered') return mode;

  if (process.env.NODE_ENV !== 'production' && mode != null) {
    console.warn(
      '[pricing] Unknown premiumPricingMode; falling back to default:',
      mode,
      DEFAULT_PREMIUM_PRICING_MODE
    );
  }

  return DEFAULT_PREMIUM_PRICING_MODE;
}

/**
 * @param {PricingCategory} [pricingCategory]
 * @returns {string}
 */
export function getPricingCurrencyForCategory(pricingCategory = DEFAULT_CATEGORY) {
  if (pricingCategory === JAPAN_CATEGORY) return JAPAN_PRICING_CURRENCY;
  return INSIGHT_PRICING_CURRENCY;
}

/**
 * @param {PricingCategory} [pricingCategory]
 * @param {PricingPlanKey} planKey
 */
export function getPricingSection(pricingCategory = DEFAULT_CATEGORY, planKey) {
  const category = pricingCategory || DEFAULT_CATEGORY;

  if (category === JAPAN_CATEGORY) {
    return insightPricingDetailsJpy.categories?.[JAPAN_CATEGORY]?.[planKey] ?? null;
  }

  if (category === 'premium') {
    const mode = getPremiumPricingMode();
    const section =
      insightPricingDetailsUsd.categories?.premium?.[mode]?.[planKey] ??
      insightPricingDetailsUsd.categories?.premium?.[DEFAULT_PREMIUM_PRICING_MODE]?.[planKey];
    return section ?? null;
  }

  const section =
    insightPricingDetailsUsd.categories?.[category]?.[planKey] ??
    insightPricingDetailsUsd.categories?.[DEFAULT_CATEGORY]?.[planKey];
  return section ?? null;
}

/**
 * @param {PricingCategory} [pricingCategory]
 * @param {PricingPlanKey} planKey
 * @returns {boolean}
 */
export function isFlatRatePricingSection(pricingCategory, planKey) {
  const section = getPricingSection(pricingCategory, planKey);
  return section?.pricingModel === 'flat';
}

/**
 * @param {PricingCategory} [pricingCategory]
 * @param {PricingPlanKey} planKey
 * @returns {number|null}
 */
export function getFlatRatePrice(pricingCategory, planKey) {
  const section = getPricingSection(pricingCategory, planKey);
  if (!section || section.pricingModel !== 'flat') return null;
  const rate = section.flatRate;
  return Number.isFinite(rate) ? rate : null;
}

/**
 * @param {PricingCategory} [pricingCategory]
 * @param {PricingPlanKey} planKey
 * @returns {number}
 */
export function getPricingDecimals(pricingCategory, planKey) {
  const section = getPricingSection(pricingCategory, planKey);
  return section?.priceDecimals ?? 2;
}

/**
 * @param {PricingPlanKey} planKey
 * @deprecated Use getPricingSection('insight', planKey)
 */
export function getInsightPricingSection(planKey) {
  return getPricingSection('insight', planKey);
}

/**
 * @param {PricingCategory} [pricingCategory]
 * @returns {string}
 */
export function getPricingFooterNote(pricingCategory = DEFAULT_CATEGORY) {
  if (pricingCategory === JAPAN_CATEGORY) {
    return insightPricingDetailsJpy.footerNote;
  }
  return insightPricingDetailsUsd.footerNote;
}

/** @deprecated Use getPricingFooterNote */
export function getInsightPricingFooterNote() {
  return getPricingFooterNote(DEFAULT_CATEGORY);
}

/**
 * @param {PricingCategory} [pricingCategory]
 * @param {PricingPlanKey} planKey
 * @returns {{ min: number, max: number }}
 */
export function getTieredPriceMinMax(pricingCategory = DEFAULT_CATEGORY, planKey) {
  const section = getPricingSection(pricingCategory, planKey);
  if (section?.pricingModel === 'flat') {
    const rate = getFlatRatePrice(pricingCategory, planKey) ?? 0;
    return { min: rate, max: rate };
  }
  const tiers = section?.tiers || [];
  const values = tiers.map((t) => t.price).filter((n) => Number.isFinite(n));
  if (!values.length) return { min: 0, max: 0 };
  return { min: Math.min(...values), max: Math.max(...values) };
}
